export type EventType =
  | "check" | "water" | "feed" | "repot" | "prune" | "mist"
  | "rotate" | "propagate" | "pest_inspect" | "photo" | "note"
  // Where a plant lives is not a field that quietly changes: Placement drives
  // every schedule in the app, so a move is recorded like anything else that
  // happened on a date.
  | "move"
  // Records that the owner knows the guide cannot schedule this Species where
  // the plant is, and is keeping it there anyway.
  | "placement_accepted"
  // Appended when an earlier event was a mistake; never sent by the log buttons.
  | "correction";

export interface CareEvent {
  id: string;
  specimen_id: string;
  type: EventType;
  occurred_at: string;
  recorded_at: string;
  source: "app" | "telegram" | "manual_backfill";
  payload: Record<string, unknown>;
  photo_key: string | null;
}

export async function fetchEvents(specimenId: string, signal?: AbortSignal): Promise<CareEvent[]> {
  const response = await fetch(`/v1/specimens/${specimenId}/events`, { signal });
  if (!response.ok) throw new Error(`The API answered ${response.status}.`);
  const body: { events: CareEvent[] } = await response.json();
  return body.events;
}

/**
 * Records something that was done. `occurredAt` is optional — omit it for
 * "just now", set it to log something that happened earlier.
 */
export async function logEvent(
  specimenId: string,
  type: EventType,
  payload: Record<string, unknown> = {},
  occurredAt?: string,
): Promise<CareEvent> {
  const response = await fetch(`/v1/specimens/${specimenId}/events`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ type, payload, occurred_at: occurredAt }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: `The API answered ${response.status}.` }));
    throw new Error(body.error ?? "The event was not recorded.");
  }
  return response.json();
}

/**
 * Records that an earlier event was a mistake.
 *
 * Nothing is deleted — a correction is appended naming the event it voids. The
 * history keeps both, so it still shows what actually happened, including the
 * error; only the engine skips the voided event.
 */
export async function correctEvent(specimenId: string, eventId: string, reason?: string): Promise<void> {
  const response = await fetch(`/v1/specimens/${specimenId}/events/${eventId}/correct`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
  if (!response.ok) throw new Error(`The API answered ${response.status}.`);
}

export async function fetchSpecimen(id: string, signal?: AbortSignal) {
  const response = await fetch(`/v1/specimens/${id}`, { signal });
  if (response.status === 404) throw new Error("That plant is not in the collection.");
  if (!response.ok) throw new Error(`The API answered ${response.status}.`);
  return response.json();
}
